import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceDot, Area, AreaChart,
} from 'recharts';

const INDICATOR_COLORS = ['#d29922', '#00d4aa', '#ec4899', '#8b5cf6', '#06b6d4', '#f85149'];

// BUY/SELL are the long lifecycle, SHORT/COVER are the short lifecycle -- kept as
// distinct colors (not just green/red) so the two are never confusable, matching
// the scheme in PriceChart.js / EquityCurveSection.js.
const TRADE_ACTION_COLOR = { BUY: '#26a641', SELL: '#f85149', SHORT: '#d29922', COVER: '#00d4aa' };
// Arrow direction follows the underlying order type (BUY/COVER are both buy-side
// fills, SELL/SHORT are both sell-side fills) -- NOT entry vs. exit.
const isBuySideAction = (action) => action === 'BUY' || action === 'COVER';
const isEntryAction = (action) => action === 'BUY' || action === 'SHORT';

// Intraday bars are stored as UTC ISO strings; convert to browser local time for display.
function fmtBarDate(d) {
  if (!d || typeof d !== 'string') return d || '';
  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  if (d.includes('T')) {
    const local = new Date(d + 'Z');
    const dow = DAYS[local.getDay()];
    const m = String(local.getMonth() + 1).padStart(2, '0');
    const day = String(local.getDate()).padStart(2, '0');
    const h = String(local.getHours()).padStart(2, '0');
    const min = String(local.getMinutes()).padStart(2, '0');
    return `${dow} ${m}/${day} ${h}:${min}`;
  }
  const parts = d.split('-');
  return parts.length >= 3 ? `${parts[1]}/${parts[2]}` : d;
}

const CandlestickShape = (props) => {
  const { x, y, width, height, payload } = props;
  if (!payload) return null;
  const { open, high, low, close } = payload;
  if (open == null || close == null || high == null || low == null) return null;
  const isUp = close >= open;
  const fill = isUp ? '#26a641' : '#f85149';
  const stroke = isUp ? '#26a641' : '#f85149';
  const rangePrice = high - low;
  if (rangePrice === 0) return null;
  const pxPerUnit = Math.abs(height) / rangePrice;
  const wickX = x + width / 2;
  const bodyTop = y + (high - Math.max(open, close)) * pxPerUnit;
  const bodyBottom = y + (high - Math.min(open, close)) * pxPerUnit;
  const bodyHeight = Math.max(bodyBottom - bodyTop, 1);
  return (
    <g>
      <line x1={wickX} y1={y} x2={wickX} y2={bodyTop} stroke={stroke} strokeWidth={1} />
      <rect x={x} y={bodyTop} width={width} height={bodyHeight} fill={fill} stroke={stroke} strokeWidth={0.5} />
      <line x1={wickX} y1={bodyBottom} x2={wickX} y2={y + Math.abs(height)} stroke={stroke} strokeWidth={1} />
    </g>
  );
};

const ReplayTooltip = ({ active, payload, isFx }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  const isUp = (d.close || 0) >= (d.open || 0);
  const dp = isFx ? 5 : 2;
  return (
    <div style={{
      background: 'rgba(13,17,23,0.92)', border: '1px solid rgba(0,212,170,0.25)',
      borderRadius: 8, padding: '8px 12px', backdropFilter: 'blur(12px)',
      fontFamily: 'var(--font-mono)', fontSize: 11, lineHeight: 1.7,
    }}>
      <div style={{ color: 'var(--muted)', marginBottom: 2 }}>{fmtBarDate(d.date)}</div>
      <div style={{ color: 'var(--text)' }}>O <span style={{ color: isUp ? '#26a641' : '#f85149' }}>{d.open?.toFixed(dp)}</span></div>
      <div style={{ color: 'var(--text)' }}>H <span style={{ color: '#58a6ff' }}>{d.high?.toFixed(dp)}</span></div>
      <div style={{ color: 'var(--text)' }}>L <span style={{ color: '#d29922' }}>{d.low?.toFixed(dp)}</span></div>
      <div style={{ color: 'var(--text)' }}>C <span style={{ color: isUp ? '#26a641' : '#f85149' }}>{d.close?.toFixed(dp)}</span></div>
      {d.volume != null && (
        <div style={{ color: 'var(--muted)', marginTop: 2 }}>Vol {Number(d.volume).toLocaleString()}</div>
      )}
    </div>
  );
};

const SPEEDS = [
  { label: '0.5×', ms: 400 },
  { label: '1×', ms: 200 },
  { label: '2×', ms: 100 },
  { label: '5×', ms: 40 },
  { label: '10×', ms: 20 },
];

const WINDOW = 80;

const replayStyles = `
  @keyframes replayFadeIn {
    from { opacity: 0; transform: scale(0.96) translateY(10px); }
    to { opacity: 1; transform: scale(1) translateY(0); }
  }
  @keyframes replayBackdropIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes replayGlow {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 1; }
  }
  @keyframes replayPulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }
  @keyframes replayTradePop {
    0% { opacity: 0; transform: translateX(-12px) scale(0.9); }
    60% { transform: translateX(2px) scale(1.02); }
    100% { opacity: 1; transform: translateX(0) scale(1); }
  }
  @keyframes replayLiveDot {
    0%, 100% { box-shadow: 0 0 0 0 rgba(0,212,170,0.5); }
    50% { box-shadow: 0 0 0 5px rgba(0,212,170,0); }
  }
  @keyframes replayScanline {
    0% { transform: translateY(-100%); }
    100% { transform: translateY(100%); }
  }
  .replay-scrubber { -webkit-appearance: none; appearance: none; height: 6px; border-radius: 3px; outline: none; cursor: pointer; }
  .replay-scrubber::-webkit-slider-runnable-track { height: 6px; border-radius: 3px; background: rgba(48,54,61,0.6); }
  .replay-scrubber::-webkit-slider-thumb {
    -webkit-appearance: none; appearance: none; width: 16px; height: 16px; border-radius: 50%;
    background: #00d4aa; border: 2px solid #0d1117; margin-top: -5px; cursor: pointer;
    box-shadow: 0 0 8px rgba(0,212,170,0.5);
    transition: box-shadow 0.2s, transform 0.15s;
  }
  .replay-scrubber::-webkit-slider-thumb:hover {
    box-shadow: 0 0 14px rgba(0,212,170,0.7); transform: scale(1.15);
  }
  .replay-scrubber::-moz-range-track { height: 6px; border-radius: 3px; background: rgba(48,54,61,0.6); border: none; }
  .replay-scrubber::-moz-range-thumb {
    width: 16px; height: 16px; border-radius: 50%; background: #00d4aa; border: 2px solid #0d1117; cursor: pointer;
    box-shadow: 0 0 8px rgba(0,212,170,0.5);
  }
  .replay-ctrl-btn {
    width: 34px; height: 34px; border-radius: 6px; border: 1px solid rgba(48,54,61,0.6);
    background: rgba(22,27,34,0.6); color: var(--text); font-size: 13px; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    backdrop-filter: blur(8px); transition: all 0.15s;
  }
  .replay-ctrl-btn:hover:not(:disabled) { background: rgba(0,212,170,0.1); border-color: rgba(0,212,170,0.3); }
  .replay-ctrl-btn:disabled { opacity: 0.3; cursor: default; }
  .replay-speed-btn {
    padding: 4px 10px; font-size: 10px; font-weight: 700; cursor: pointer; border-radius: 4px;
    font-family: var(--font-mono); letter-spacing: 0.3px; transition: all 0.15s;
  }
  .replay-speed-btn:hover { transform: translateY(-1px); }
`;

export default function ReplayModal({ priceChartData, tradeEvents, indicatorNames, priceByDate, equityCurve, symbol, onClose, isFx }) {
  const dp = isFx ? 5 : 2;
  const fmtP = (v) => v == null ? '' : isFx ? Number(v).toFixed(5) : '$' + Number(v).toFixed(2);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speedIdx, setSpeedIdx] = useState(1);
  const speedRef = useRef(1);
  const total = priceChartData.length;

  useEffect(() => { speedRef.current = speedIdx; }, [speedIdx]);

  const [priceMin, priceMax] = useMemo(() => {
    let lo = Infinity, hi = -Infinity;
    for (const d of priceChartData) {
      if (d.low != null && d.low < lo) lo = d.low;
      if (d.high != null && d.high > hi) hi = d.high;
    }
    const pad = (hi - lo) * 0.03;
    return [lo - pad, hi + pad];
  }, [priceChartData]);

  const dateToIdx = useMemo(() => {
    const map = {};
    priceChartData.forEach((d, i) => { map[d.date] = i; });
    return map;
  }, [priceChartData]);

  const equityDateToIdx = useMemo(() => {
    const map = {};
    (equityCurve || []).forEach((d, i) => { map[d.date] = i; });
    return map;
  }, [equityCurve]);

  const [eqMin, eqMax] = useMemo(() => {
    if (!equityCurve || equityCurve.length === 0) return [0, 0];
    let lo = Infinity, hi = -Infinity;
    for (const d of equityCurve) {
      if (d.value < lo) lo = d.value;
      if (d.value > hi) hi = d.value;
    }
    const pad = (hi - lo) * 0.05 || 100;
    return [lo - pad, hi + pad];
  }, [equityCurve]);

  const windowStart = Math.max(0, currentIdx - WINDOW + 1);
  const visibleData = priceChartData.slice(windowStart, currentIdx + 1);
  const visibleTrades = tradeEvents.filter((t) => {
    const idx = dateToIdx[t.date];
    return idx != null && idx >= windowStart && idx <= currentIdx;
  });

  const visibleEquity = useMemo(() => {
    if (!equityCurve || equityCurve.length === 0) return [];
    const startDate = visibleData[0]?.date;
    const endDate = visibleData[visibleData.length - 1]?.date;
    if (!startDate || !endDate) return [];
    return equityCurve.filter((d) => d.date >= startDate && d.date <= endDate);
  }, [equityCurve, visibleData]);

  const lastTrade = visibleTrades.length > 0 ? visibleTrades[visibleTrades.length - 1] : null;
  const lastTradeIdx = lastTrade ? dateToIdx[lastTrade.date] : -1;
  const isTradeNew = lastTradeIdx === currentIdx;

  const currentBar = priceChartData[currentIdx];
  const prevBar = currentIdx > 0 ? priceChartData[currentIdx - 1] : null;
  const dayChange = currentBar && prevBar ? currentBar.close - prevBar.close : 0;
  const dayChangePct = prevBar && prevBar.close ? (dayChange / prevBar.close) * 100 : 0;

  // Current equity value
  const currentEquity = useMemo(() => {
    if (!equityCurve || !currentBar) return null;
    const eq = equityCurve.find((e) => e.date === currentBar.date);
    return eq ? eq.value : null;
  }, [equityCurve, currentBar]);

  const startEquity = equityCurve && equityCurve.length > 0 ? equityCurve[0].value : null;
  const totalReturn = currentEquity && startEquity ? ((currentEquity - startEquity) / startEquity) * 100 : null;

  const progressPct = total > 1 ? (currentIdx / (total - 1)) * 100 : 0;

  const advance = useCallback(() => {
    setCurrentIdx((prev) => {
      if (prev >= total - 1) {
        setPlaying(false);
        return prev;
      }
      return prev + 1;
    });
  }, [total]);

  useEffect(() => {
    if (!playing) return;
    let id;
    const tick = () => {
      advance();
      id = setTimeout(tick, SPEEDS[speedRef.current].ms);
    };
    id = setTimeout(tick, SPEEDS[speedRef.current].ms);
    return () => clearTimeout(id);
  }, [playing, advance]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <>
      <style>{replayStyles}</style>
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'replayBackdropIn 0.25s ease-out',
      }} onClick={onClose}>
        <div
          onClick={(e) => e.stopPropagation()}
          className="replay-modal-content"
          style={{
            background: 'linear-gradient(165deg, rgba(33,38,45,0.95) 0%, rgba(13,17,23,0.98) 100%)',
            borderRadius: 16, width: '92vw', maxWidth: 1150,
            maxHeight: '92vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
            boxShadow: '0 0 0 1px rgba(0,212,170,0.1), 0 25px 80px rgba(0,0,0,0.6), 0 0 40px rgba(0,212,170,0.05)',
            border: '1px solid rgba(48,54,61,0.6)',
            animation: 'replayFadeIn 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
            position: 'relative',
          }}
        >
          {/* Subtle top accent line */}
          <div style={{
            position: 'absolute', top: 0, left: '10%', right: '10%', height: 1,
            background: 'linear-gradient(90deg, transparent, rgba(0,212,170,0.4), transparent)',
          }} />

          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '16px 24px', borderBottom: '1px solid rgba(48,54,61,0.5)',
            background: 'rgba(13,17,23,0.3)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              {/* Live status indicator */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '5px 14px', borderRadius: 20,
                background: playing ? 'rgba(0,212,170,0.1)' : 'rgba(139,148,158,0.08)',
                border: `1px solid ${playing ? 'rgba(0,212,170,0.25)' : 'rgba(48,54,61,0.5)'}`,
                transition: 'all 0.3s',
              }}>
                <div style={{
                  width: 7, height: 7, borderRadius: '50%',
                  background: playing ? '#00d4aa' : 'var(--muted)',
                  animation: playing ? 'replayLiveDot 1.5s infinite' : 'none',
                  transition: 'background 0.3s',
                }} />
                <span style={{
                  fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1,
                  color: playing ? '#00d4aa' : 'var(--muted)',
                  fontFamily: 'var(--font-mono)',
                }}>
                  {playing ? 'LIVE' : 'PAUSED'}
                </span>
              </div>

              <span style={{ fontSize: 17, fontWeight: 800, color: 'var(--text)', letterSpacing: 0.5 }}>
                {symbol}
              </span>

              {currentBar && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                  <span style={{ color: 'var(--muted)' }}>{currentBar.date}</span>
                  <span style={{ color: 'var(--text)' }}>{fmtP(currentBar.close)}</span>
                  {prevBar && (
                    <span style={{
                      color: dayChange >= 0 ? '#26a641' : '#f85149',
                      fontWeight: 700,
                    }}>
                      {dayChange >= 0 ? '+' : ''}{dayChange.toFixed(dp)} ({dayChangePct >= 0 ? '+' : ''}{dayChangePct.toFixed(2)}%)
                    </span>
                  )}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {/* Stats pills */}
              {currentEquity != null && (
                <div style={{
                  display: 'flex', gap: 8, fontSize: 11, fontFamily: 'var(--font-mono)',
                }}>
                  <div style={{
                    padding: '4px 10px', borderRadius: 6,
                    background: 'rgba(0,212,170,0.08)', border: '1px solid rgba(0,212,170,0.15)',
                  }}>
                    <span style={{ color: 'var(--muted)' }}>Equity </span>
                    <span style={{ color: 'var(--teal)', fontWeight: 700 }}>${currentEquity.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                  </div>
                  {totalReturn != null && (
                    <div style={{
                      padding: '4px 10px', borderRadius: 6,
                      background: totalReturn >= 0 ? 'rgba(38,166,65,0.08)' : 'rgba(248,81,73,0.08)',
                      border: `1px solid ${totalReturn >= 0 ? 'rgba(38,166,65,0.15)' : 'rgba(248,81,73,0.15)'}`,
                    }}>
                      <span style={{ color: 'var(--muted)' }}>P&L </span>
                      <span style={{ color: totalReturn >= 0 ? '#26a641' : '#f85149', fontWeight: 700 }}>
                        {totalReturn >= 0 ? '+' : ''}{totalReturn.toFixed(2)}%
                      </span>
                    </div>
                  )}
                </div>
              )}
              <button onClick={onClose} style={{
                background: 'rgba(139,148,158,0.08)', border: '1px solid rgba(48,54,61,0.5)',
                borderRadius: 8, width: 32, height: 32,
                fontSize: 18, cursor: 'pointer', color: 'var(--muted)', lineHeight: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.15s',
              }}
                onMouseEnter={(e) => { e.target.style.background = 'rgba(248,81,73,0.15)'; e.target.style.color = '#f85149'; e.target.style.borderColor = 'rgba(248,81,73,0.3)'; }}
                onMouseLeave={(e) => { e.target.style.background = 'rgba(139,148,158,0.08)'; e.target.style.color = 'var(--muted)'; e.target.style.borderColor = 'rgba(48,54,61,0.5)'; }}
              >×</button>
            </div>
          </div>

          {/* Trade callout */}
          <div style={{ minHeight: 36, padding: '0 24px', display: 'flex', alignItems: 'center' }}>
            {isTradeNew && lastTrade && (() => {
              const c = TRADE_ACTION_COLOR[lastTrade.action] || '#8b949e';
              return (
                <div style={{
                  padding: '6px 16px', borderRadius: 8, fontSize: 13, fontWeight: 800,
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: `linear-gradient(135deg, ${c}2e 0%, ${c}0f 100%)`,
                  border: `1px solid ${c}4d`,
                  color: c,
                  animation: 'replayTradePop 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                  fontFamily: 'var(--font-mono)',
                  letterSpacing: 0.5,
                }}>
                  <span style={{ fontSize: 16 }}>{isBuySideAction(lastTrade.action) ? '▲' : '▼'}</span>
                  {lastTrade.action} @ {fmtP(lastTrade.price)}
                </div>
              );
            })()}
          </div>

          {/* Chart area */}
          <div style={{ flex: 1, padding: '0 24px', minHeight: 0, position: 'relative' }}>
            {/* Faint scanline overlay */}
            <div style={{
              position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, overflow: 'hidden', opacity: 0.02,
              background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,212,170,0.3) 2px, rgba(0,212,170,0.3) 4px)',
            }} />

            <ResponsiveContainer width="100%" height={340}>
              <ComposedChart data={visibleData} margin={{ top: 10, right: 10, bottom: 0, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(48,54,61,0.4)" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--muted)' }} interval="preserveStartEnd" tickFormatter={fmtBarDate} />
                <YAxis yAxisId="price" tick={{ fontSize: 11, fill: 'var(--muted)' }} domain={[priceMin, priceMax]}
                  tickFormatter={(v) => isFx ? v.toFixed(5) : (v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v.toFixed(0)}`)} />
                <YAxis yAxisId="volume" orientation="right" hide domain={[0, (dataMax) => dataMax * 4]} />
                <Tooltip content={<ReplayTooltip isFx={isFx} />} />
                <Bar yAxisId="volume" dataKey="volume" fill="var(--subtle)" opacity={0.2} barSize={2} legendType="none" isAnimationActive={false} />
                <Bar yAxisId="price" dataKey="candleRange" barSize={Math.max(2, Math.min(8, 600 / visibleData.length))}
                  shape={<CandlestickShape />} legendType="none" isAnimationActive={false} />
                {indicatorNames.map((name, i) => (
                  <Line key={name} yAxisId="price" type="monotone" dataKey={name}
                    stroke={INDICATOR_COLORS[i % INDICATOR_COLORS.length]}
                    dot={false} strokeWidth={1.5} isAnimationActive={false} />
                ))}
                {visibleTrades.map((t, i) => {
                  const row = priceByDate[t.date];
                  if (!row) return null;
                  return (
                    <ReferenceDot key={`rt-${i}`} yAxisId="price" x={t.date}
                      y={isEntryAction(t.action) ? row.low * 0.995 : row.high * 1.005}
                      fill={TRADE_ACTION_COLOR[t.action] || '#8b949e'}
                      stroke="var(--bg)" strokeWidth={2} isFront />
                  );
                })}
              </ComposedChart>
            </ResponsiveContainer>

            {/* Mini equity curve */}
            {visibleEquity.length > 0 && (
              <ResponsiveContainer width="100%" height={70}>
                <AreaChart data={visibleEquity} margin={{ top: 2, right: 10, bottom: 0, left: 10 }}>
                  <defs>
                    <linearGradient id="eqGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--teal)" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="var(--teal)" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <YAxis hide domain={[eqMin, eqMax]} />
                  <XAxis dataKey="date" hide />
                  <Tooltip formatter={(v) => [`$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 'Equity']}
                    contentStyle={{ fontSize: 11, padding: '4px 10px', background: 'rgba(13,17,23,0.92)', border: '1px solid rgba(0,212,170,0.2)', borderRadius: 6, backdropFilter: 'blur(8px)' }} />
                  <Area type="monotone" dataKey="value" stroke="var(--teal)" strokeWidth={1.5}
                    fill="url(#eqGrad)" isAnimationActive={false} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Progress bar */}
          <div style={{ padding: '0 24px', height: 3 }}>
            <div style={{
              width: '100%', height: '100%', borderRadius: 2,
              background: 'rgba(48,54,61,0.3)', overflow: 'hidden',
            }}>
              <div style={{
                height: '100%', borderRadius: 2,
                width: `${progressPct}%`,
                background: 'linear-gradient(90deg, #00d4aa, #58a6ff)',
                boxShadow: '0 0 10px rgba(0,212,170,0.4)',
                transition: 'width 0.1s linear',
              }} />
            </div>
          </div>

          {/* Controls */}
          <div className="replay-controls" style={{
            padding: '14px 24px 18px',
            display: 'flex', alignItems: 'center', gap: 10,
            background: 'rgba(13,17,23,0.3)',
          }}>
            {/* Play/Pause */}
            <button onClick={() => {
              if (currentIdx >= total - 1) { setCurrentIdx(0); setPlaying(true); }
              else setPlaying((p) => !p);
            }} style={{
              width: 44, height: 44, borderRadius: '50%', border: 'none',
              background: playing
                ? 'linear-gradient(135deg, #f85149, #da3633)'
                : 'linear-gradient(135deg, #00d4aa, #00b894)',
              color: '#0d1117',
              fontSize: 17, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: playing
                ? '0 0 20px rgba(248,81,73,0.3)'
                : '0 0 20px rgba(0,212,170,0.3)',
              transition: 'all 0.2s',
              fontWeight: 900,
            }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.08)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
            >
              {playing ? '⏸' : (currentIdx >= total - 1 ? '↻' : '▶')}
            </button>

            {/* Step backward/forward */}
            <button className="replay-ctrl-btn"
              onClick={() => { setPlaying(false); setCurrentIdx((p) => Math.max(0, p - 1)); }}
              disabled={currentIdx <= 0}>◀</button>
            <button className="replay-ctrl-btn"
              onClick={() => { setPlaying(false); setCurrentIdx((p) => Math.min(total - 1, p + 1)); }}
              disabled={currentIdx >= total - 1}>▶</button>

            {/* Progress scrubber */}
            <input
              className="replay-scrubber"
              type="range" min={0} max={total - 1} value={currentIdx}
              onChange={(e) => { setPlaying(false); setCurrentIdx(Number(e.target.value)); }}
              style={{ flex: 1, background: 'transparent' }}
            />

            {/* Bar counter */}
            <span style={{
              fontSize: 11, color: 'var(--muted)', minWidth: 70, textAlign: 'right',
              fontFamily: 'var(--font-mono)', fontWeight: 600,
              padding: '4px 10px', borderRadius: 6,
              background: 'rgba(48,54,61,0.2)', border: '1px solid rgba(48,54,61,0.3)',
            }}>
              {currentIdx + 1} / {total}
            </span>

            {/* Speed selector */}
            <div className="replay-speeds" style={{ display: 'flex', gap: 3 }}>
              {SPEEDS.map((s, i) => (
                <button key={s.label} className="replay-speed-btn" onClick={() => setSpeedIdx(i)} style={{
                  border: speedIdx === i ? '1.5px solid rgba(0,212,170,0.5)' : '1px solid rgba(48,54,61,0.5)',
                  background: speedIdx === i
                    ? 'linear-gradient(135deg, rgba(0,212,170,0.15), rgba(0,212,170,0.05))'
                    : 'rgba(22,27,34,0.5)',
                  color: speedIdx === i ? '#00d4aa' : 'var(--muted)',
                  boxShadow: speedIdx === i ? '0 0 8px rgba(0,212,170,0.15)' : 'none',
                }}>{s.label}</button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
